import type { Action } from 'react-sweet-state';

import { initialState } from './constants';
import { useGetPreviewPanelsContext } from './context';
import type { OpenParams, PreviewPanelsApiState } from './types';

export const previewPanelsActions = {
	setPanelWidth:
		(width: number): Action<PreviewPanelsApiState> =>
		({ setState }) => {
			setState({ width });
		},
	close:
		(onClose?: () => void): Action<PreviewPanelsApiState> =>
		({ setState, getState }) => {
			const { closePanelSlot } = getState();

			// Call both the user provided onClose and the state's onClose
			onClose?.();

			// Call the closePanelSlot to handle UI closing
			closePanelSlot();

			// Reset state to initial values
			setState(initialState);
		},
	open:
		({
			panel,
			closePanelSlot,
			openPanelSlot,
			onOpen,
			width,
		}: OpenParams): Action<PreviewPanelsApiState> =>
		({ getState, setState }) => {
			// If same panel is already open, do nothing
			if (getState().panel.id === panel.id) {
				return;
			}

			// Close current panel
			closePanelSlot();

			// Update state with new panel data
			setState({
				panel,
				width: width || getState().width,
				closePanelSlot,
				openPanelSlot,
				onOpen,
			});

			// Open new panel
			openPanelSlot();

			// Call user's callback
			onOpen();
		},
};

export const usePreviewPanelsActions = () => {
	const { dispatch } = useGetPreviewPanelsContext();
	const setPanelWidth = (width: number) => {
		dispatch({ type: 'SET_PANEL_WIDTH', width });
	};
	const close = (onClose?: () => void) => {
		dispatch({ type: 'CLOSE', onClose });
	};
	const open = (params: OpenParams) => {
		dispatch({ type: 'OPEN', params });
	};
	return { setPanelWidth, close, open };
};
